import { MapContainer, TileLayer, Polygon, Polyline, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { Undo2, Trash2 } from "lucide-react";

const makeIcon = (idx) =>
  L.divIcon({
    className: "",
    iconSize: [22, 22],
    iconAnchor: [11, 11],
    html: `<div style="width:22px;height:22px;border-radius:50%;background:hsl(141 78% 27%);border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;color:#fff;font:600 10px Inter,sans-serif">${idx + 1}</div>`,
  });

function ClickCollector({ onAdd }) {
  useMapEvents({ click: (e) => onAdd([e.latlng.lat, e.latlng.lng]) });
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  if (target) map.flyTo(target, Math.max(map.getZoom(), 16), { duration: 0.8 });
  return null;
}

function TileLayerFor({ tile }) {
  if (tile === "satellite") {
    return <TileLayer attribution="Tiles © Esri" url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" maxZoom={20} />;
  }
  return <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://tile.openstreetmap.org/{z}/{y}/{x}.png" maxZoom={19} />;
}

function MapToolbar({ onUndo, onClear, disabled }) {
  return (
    <div className="absolute top-3 right-3 z-[500] flex flex-col gap-2">
      <button data-testid="map-undo-btn" onClick={onUndo} disabled={disabled}
        className="h-9 px-3 rounded-md border border-border bg-card/95 backdrop-blur shadow hover:bg-accent text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
        <Undo2 className="h-3.5 w-3.5" strokeWidth={2} /> Undo
      </button>
      <button data-testid="map-clear-btn" onClick={onClear} disabled={disabled}
        className="h-9 px-3 rounded-md border border-border bg-card/95 backdrop-blur shadow hover:bg-destructive/10 hover:border-destructive/40 hover:text-destructive text-xs font-semibold flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
        <Trash2 className="h-3.5 w-3.5" strokeWidth={2} /> Clear
      </button>
    </div>
  );
}

function MapHint({ count }) {
  if (count >= 3) return null;
  const msg = count === 0 ? "Tap the map to add points" : `Add ${3 - count} more point${3 - count === 1 ? "" : "s"} to close the shape`;
  return (
    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-[500] px-3 py-1.5 rounded-full bg-card/95 backdrop-blur border border-border shadow text-xs font-medium text-muted-foreground pointer-events-none">
      {msg}
    </div>
  );
}

export default function MapCanvas({ center, tile, points, onAdd, onUndo, onClear }) {
  const closed = points.length >= 3;
  return (
    <div className="relative rounded-lg border border-border bg-card overflow-hidden" style={{ height: "68vh", minHeight: 420 }} data-testid="map-container">
      <MapContainer center={center} zoom={16} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
        <TileLayerFor tile={tile} />
        <FlyTo target={center} />
        <ClickCollector onAdd={onAdd} />
        {points.map((p, i) => (
          <Marker key={`${p[0].toFixed(6)}-${p[1].toFixed(6)}-${i}`} position={p} icon={makeIcon(i)} />
        ))}
        {points.length === 2 && (
          <Polyline positions={points} pathOptions={{ color: "#0F7A3A", weight: 3, dashArray: "6 6" }} />
        )}
        {closed && (
          <Polygon positions={points} pathOptions={{ color: "#0F7A3A", fillColor: "#0F7A3A", fillOpacity: 0.2, weight: 3 }} />
        )}
      </MapContainer>
      <MapToolbar onUndo={onUndo} onClear={onClear} disabled={points.length === 0} />
      <MapHint count={points.length} />
    </div>
  );
}
